import { useContext, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ArrowLeft from '../../Assets/Arrow left.svg';
import ImageAdd from '../../Assets/Add image.svg';
import { CropImageContext } from '../../Context/CropImageContext';
import CropImage from '../../Components/CropImage/CropImage';
import { getMediaUrl, loadImage } from '../../lib/utils/utils';
import { v4 as uuidv4 } from 'uuid';
import imageCompression from 'browser-image-compression';
import { createCategory } from '../../lib/utils/apiCalls';
import { showToast } from '../../Components/Toaster/Toaster';
import LoadingTransparent from '../../Components/Loading/LoadingTransparent';

function AddCategory() {
    const location = useLocation();
    const navigate = useNavigate();

    const handleCancel = () => {
        navigate(`${location.pathname.split('/').slice(0, -2).join('/')}`);
    }

    const [loading, setLoading] = useState(false);
    const { setImageToCrop, croppedImage, setCroppedImage, isImageCropped, setIsImageCropped, modalOpen, setModalOpen, setAspectRatio } = useContext(CropImageContext);

    const defaultFormInputs = {
        name: '',
        description: '',
    }

    const defaultFormErrors = {
        name: '',
        description: '',
        image: '',
    }

    const [formInputs, setFormInputs] = useState(defaultFormInputs);
    const [formErrors, setFormErrors] = useState(defaultFormErrors);

    const handleChange = (e) => {
        const {name, value} = e.target;
        setFormInputs({ ...formInputs, [name]: value });
    }

    const [image, setImage] = useState({});

    // const [modalOpen, setModalOpen] = useState(false);

    const handleImageSelection = async (e) => {
        const file = e.target.files[0];
        setImageToCrop(file);
        setAspectRatio(1 / 1);
        setModalOpen(true);
        e.target.value = null;
    }

    const handleDrop = (e) => {
        e.preventDefault();
        const file = e.dataTransfer.files[0];
        setImageToCrop(file);
        setAspectRatio(1 / 1);
        setModalOpen(true);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const processFile = async (file) => {
        if(file) {
            const img = await loadImage(file);
            const selectedImage = {
                id: uuidv4(),
                url: img.src,
                file: file
            };
            setImage(selectedImage);
        }
        else {
            return;
        }
    }

    useEffect(() => {
        if(isImageCropped) {
            processFile(croppedImage);
            setIsImageCropped(false);
            setCroppedImage({});
            setModalOpen(false);
        }

        // eslint-disable-next-line
    }, [isImageCropped]);

    const [modalVisible, setModalVisible] = useState(false);

    useEffect(() => {
        if(modalOpen) {
            setModalVisible(true);
        } else {
            setModalVisible(false);
        }
    }, [modalOpen]);

    const [hoverOnImage, setHoverOnImage] = useState(false);
    const [buttonsVisible, setButtonsVisible] = useState(false);

    useEffect(() => {
        if(hoverOnImage) {
            setButtonsVisible(true);
        } else {
            setButtonsVisible(false);
        }
    }, [hoverOnImage]);

    const handleDeleteImage = () => {
        setImage({});
    }

    const handleReplaceImage = async (e) => {
        setHoverOnImage(false);
        const file = e.target.files[0];
        setImageToCrop(file);
        setModalOpen(true);
        e.target.value = null;
    }

    const handleSaveCategory = async () => {
        const validationErrors = {};

        if(!formInputs.name.trim()) {
            validationErrors.name = 'Required field';
        }

        if(Object.keys(image).length === 0) {
            validationErrors.image = 'Image is required';
        }

        if(formInputs.description.length > 255) {
            validationErrors.description = 'Max 255 characters';
        }

        if(Object.keys(validationErrors).length === 0) {
            setFormErrors(defaultFormErrors);
            setLoading(true);

            try {
                let url;

                if(image.file.size > 1048576) {
                    try {
                        let compressedBlob = await imageCompression(image.file, {
                            maxSizeMB: 1,
                            maxWidthOrHeight: 1920,
                            useWebWorker: true
                        });
    
                        let compressedFile = new File([compressedBlob], image.file.name, { type: image.file.type, lastModified: Date.now() });
                    
                        if (compressedFile) {
                            url = await getMediaUrl(compressedFile);
                        }
                        else {
                            url = await getMediaUrl(image.file);
                        }
                    }
                    catch(err) {
                        url = await getMediaUrl(image.file);
                    }
                }
                else {
                    url = await getMediaUrl(image.file);
                }

                const callData = {
                    categoryName: formInputs.name,
                    categoryDesc: formInputs.description,
                    categoryImgUrl: [url],
                    isVisible: true,
                }

                const response = await createCategory(callData);
                if(response?.status === 200) {
                    showToast('Category created.', 'success');
                    setLoading(false);
                    handleCancel();
                }
                else {
                    showToast('Error creating category.', 'error');
                    setLoading(false);
                }
            }
            catch(err) {
                showToast('Error creating category.', 'error');
                setLoading(false);
            }
        }
        else {
            setFormErrors(validationErrors);
            setLoading(false);
            return;
        }
    }

    return (
        <div className="size-full overflow-y-scroll pb-16 outline-none">
            <div className="select-none flex items-center gap-2 relative m-[20px_19px_24px_20px]">
                <Link to={`${location.pathname.split('/').slice(0, -2).join('/')}`} className="p-[10px] flex items-center justify-center">
                    <img className='size-6 text-[#666666]' src={ArrowLeft} alt="" />
                </Link>

                <h1 className="p-[6px] text-[#121212] font-bold leading-8 text-[24px]">Add Category</h1>
            </div>

            <div className="flex flex-col gap-6 mt-4 mx-5 w-[70%]">
                <div className="flex flex-col gap-3">
                    <div className="flex flex-col gap-2 border border-[#EAEAEA] rounded-xl p-5">
                        <div className="flex flex-col gap-2">
                            <div className="flex items-center justify-between">
                                <label className='font-medium text-[18px] leading-6 text-[#121212] w-fit cursor-pointer select-none' htmlFor="name">Category name</label>
                            
                                <p className='text-[14px] leading-5 text-[#DC2626] select-none'>{formErrors.name}</p>
                            </div>

                            <input 
                                className='outline-none px-3 py-4 rounded-[9px] bg-[#F5F5F5] border border-[#EAEAEA] placeholder:font-normal placeholder:text-[#666666] text-base font-medium text-[#121212] focus:border-primaryColor'
                                id='name' 
                                name='name' 
                                type="text" 
                                placeholder='Enter category name' 
                                value={formInputs.name} 
                                onChange={handleChange} 
                            />
                        </div>

                        <div className="flex flex-col gap-2">
                            <div className="flex items-center justify-between">
                                <label className='font-medium text-[18px] leading-6 text-[#121212] w-fit cursor-pointer select-none' htmlFor="description">Description</label>

                                <p className='text-[14px] leading-5 text-[#DC2626] select-none'>{formErrors.description}</p>
                            </div>

                            <textarea 
                                name="description" 
                                id="description" 
                                className='outline-none px-3 py-4 rounded-[9px] bg-[#F5F5F5] border border-[#EAEAEA] h-44 focus:border-primaryColor' 
                                placeholder='Write a short description about this category(Optional)' 
                                value={formInputs.description} 
                                onChange={handleChange} 
                            />
                        </div>
                    </div>

                    <div className="flex flex-col gap-4 border border-[#EAEAEA] p-5 rounded-xl">
                        <div className="flex items-center justify-between">
                            <h1 className='font-semibold text-[18px] leading-[26px] select-none'>
                                Add Image <span className='text-base text-[#666666]'>&#40;1080px * 1080px&#41;</span>
                            </h1>

                            <p className='text-[14px] leading-5 text-[#DC2626] select-none'>{formErrors.image}</p>
                        </div>

                        <div 
                            onDrop={handleDrop} 
                            onDragOver={handleDragOver} 
                            onDragLeave={handleDragLeave} 
                            className="p-3 flex flex-wrap gap-3 rounded-xl bg-[#F5F5F5]"
                        >
                            {
                                Object.keys(image).length === 0 ? 
                                <label htmlFor='product-image' className="cursor-pointer border-dashed bg-[#E5E5E5] border-2 border-[#D1D5DB] size-[140px] rounded-lg flex flex-col items-center justify-center gap-2">
                                    <div className="w-12 h-12 flex items-center justify-center">
                                        <img src={ImageAdd} alt="" />
                                    </div>

                                    <p className='font-medium text-sm text-[#4B5563] w-[90%] text-center'>
                                        <span className='text-[#4F46E5]'>Click to Upload</span> or Drag or drop
                                    </p>
                                </label> : 
                                <div 
                                    onMouseEnter={() => setHoverOnImage(true)} 
                                    onMouseLeave={() => setHoverOnImage(false)} 
                                    className='relative size-[140px] rounded-lg overflow-hidden'
                                >
                                    <img className='size-full object-cover' src={image.url} alt="" />

                                    <div className={`${buttonsVisible ? 'opacity-100 visible' : 'opacity-0 invisible'} transition-all delay-100 ease-in-out w-full h-full bg-[#00000066] absolute top-0 left-0 items-center justify-center flex flex-col gap-2`}>
                                        <label htmlFor={image.id} className='select-none bg-[#FFFFFF] w-3/5 py-[6px] text-sm font-semibold rounded text-center cursor-pointer'>Replace</label>
                                        
                                        <button onClick={handleDeleteImage} className='select-none bg-[#FFFFFF] w-3/5 py-[6px] text-sm font-semibold rounded'>Delete</button>

                                        <input 
                                            className='hidden' 
                                            id={image.id} 
                                            type="file" 
                                            accept="image/*" 
                                            onChange={handleReplaceImage} 
                                        />
                                    </div>
                                </div>
                            }

                            <input 
                                onChange={handleImageSelection} 
                                className='hidden'
                                type="file" 
                                accept="image/*" 
                                name="product-image" 
                                id="product-image" 
                            />
                        </div>
                    </div>
                </div>

                <div className="ml-auto flex items-center gap-3">
                    <button onClick={handleCancel} className='px-4 py-2 rounded bg-[#FFFFFF] font-semibold text-base text-[#808080]'>Cancel</button>

                    <button onClick={handleSaveCategory} className='px-4 py-2 rounded bg-primaryColor hover:bg-primaryAccent font-semibold text-base text-buttonText'>Save Category</button>
                </div>
            </div>

            {
                modalOpen && 
                <div className={`${modalVisible ? 'opacity-100 visible bg-[#00000080]' : 'opacity-0 invisible'} transition-all duration-100 ease-in-out fixed z-50 w-full h-full top-0 left-0 flex justify-center items-center`}>
                    <CropImage />
                </div>
            }

            {loading && <LoadingTransparent />}
        </div>
    );
}

export default AddCategory;
